import React from 'react';
import { Share2, Lock, QrCode, Search, Briefcase } from 'lucide-react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';

type FeatureCardProps = {
    icon: React.ElementType; // Type for the Lucide icon component
    title: string;
    description: string;
};

// Applied the FeatureCardProps type
const FeatureCard = ({ icon: Icon, title, description }: FeatureCardProps) => (
    <div className="bg-white p-8 rounded-3xl shadow-xl hover:shadow-2xl transition duration-300 transform hover:-translate-y-1 border border-gray-100">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 mb-4">
            <Icon className="w-6 h-6" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-600">{description}</p>
    </div>
);

const handleDownloadClick = (): void => {
    if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
        window.location.href = "https://testflight.apple.com/join/edxyCJeP";
    } else {
        window.location.href =
            "https://play.google.com/store/apps/details?id=com.lifelongcards.app";
    }
};

const HomePage = () => {
    return (
        <div className="min-h-screen bg-gray-50 font-sans antialiased text-gray-800">
            <script src="https://cdn.tailwindcss.com"></script>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
        body { font-family: 'Inter', sans-serif; }
      `}</style>

            <Header />

            <main className="pt-20">
                <section className="relative overflow-hidden pt-16 pb-20 md:pt-32 md:pb-36 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center">
                            <span className="inline-block bg-indigo-100 text-indigo-600 text-sm font-semibold px-3 py-1 rounded-full mb-4">
                                The Future of Networking
                            </span>
                            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight text-gray-900 leading-tight mb-6">
                                Connect Instantly, <span className="text-indigo-600">Share Securely</span>.
                            </h1>
                            <p className="max-w-3xl mx-auto text-xl text-gray-600 mb-10">
                                Olakh transforms contact sharing into a connected, digital experience. Share personal and business contact cards effortlessly via QR, and stay in control of your data.
                            </p>

                            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                                <a
                                    href="https://play.google.com/store/apps/details?id=com.lifelongcards.app"
                                    target="_blank"
                                    className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-lg font-bold rounded-xl text-white transition duration-300 transform hover:scale-[1.02]"
                                    rel="noreferrer"
                                ><img
                                        className='image-align'
                                        alt="Get it on google play"
                                        style={{ height: "60px" }}
                                        src="google-play-badge.png"
                                    /></a>
                                <a href="https://testflight.apple.com/join/edxyCJeP" target="_blank" rel="noreferrer"
                                    className="inline-flex items-center justify-center px-8 py-3 text-lg font-bold rounded-xl text-white transition duration-300 transform hover:scale-[1.02]"
                                ><img
                                        className='image-align'
                                        alt="Get it on App Store"
                                        style={{ height: "45px" }}
                                        src="apple-store-badge.png"
                                    /></a>
                            </div>
                        </div>
                    </div>
                </section>
                <div className="w-full h-20 bg-white -mt-10 transform skew-y-1"></div> {/* Visual Separator */}
                <section id="features" className="py-20 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-center text-gray-900 mb-12">
                            How Olakh Reimagines Contact Sharing
                        </h2>
                        <div className="grid md:grid-cols-3 gap-8">
                            <FeatureCard
                                icon={QrCode}
                                title="Instant QR Share"
                                description="Forget typing numbers. Generate a QR code for instant, contactless sharing of your customized contact card with anyone."
                            />
                            <FeatureCard
                                icon={Search}
                                title="Seamless Search"
                                description="Easily find and request contact connections with other Olakh users by name within the secure network."
                            />
                            <FeatureCard
                                icon={Share2}
                                title="Always Connected"
                                description="When a contact updates their information on Olakh, your card automatically updates, so you always have the latest details."
                            />
                        </div>
                    </div>
                </section>
                <section id="security" className="py-20 bg-indigo-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
                        <div className="order-2 md:order-1">
                            <Lock className="w-16 h-16 text-indigo-600 mb-4 p-3 bg-indigo-200 rounded-2xl" />
                            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
                                Your Data, Your Approval.
                            </h2>
                            <p className="text-xl text-gray-600 mb-6">
                                Privacy is our foundation. When someone requests your contact card, you must approve the connection before any data is shared or visible. No unsolicited contacts, ever.
                            </p>
                            <ul className="space-y-3 text-gray-600 text-lg">
                                <li className="flex items-center">
                                    <span className="text-indigo-600 mr-2 font-bold text-2xl">•</span> Full control over who sees your data.
                                </li>
                                <li className="flex items-center">
                                    <span className="text-indigo-600 mr-2 font-bold text-2xl">•</span> Granular permissions for different cards.
                                </li>
                                <li className="flex items-center">
                                    <span className="text-indigo-600 mr-2 font-bold text-2xl">•</span> Secure, encrypted data handling.
                                </li>
                            </ul>
                        </div>
                        <div className="order-1 md:order-2 flex justify-center">
                            <div className="w-full max-w-md h-72 bg-white rounded-3xl p-6 shadow-2xl flex flex-col items-center justify-center text-center">
                                <Lock className="w-16 h-16 text-red-500 mb-4" />
                                <p className="text-2xl font-bold text-gray-800">Connection Pending</p>
                                <p className="text-gray-500 mt-2">User: Jane Doe is requesting to connect.</p>
                                <div className="mt-6 flex space-x-4">
                                    <button className="px-6 py-2 bg-green-500 text-white font-semibold rounded-full hover:bg-green-600">Approve</button>
                                    <button className="px-6 py-2 bg-gray-300 text-gray-800 font-semibold rounded-full hover:bg-gray-400">Deny</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <section id="business" className="py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
                        <div className="flex justify-center">
                            <div className="w-full max-w-md h-72 bg-gray-100 rounded-3xl p-6 shadow-xl flex flex-col items-center justify-center text-center border-4 border-indigo-100">
                                <Briefcase className="w-16 h-16 text-gray-800 mb-4" />
                                <p className="text-2xl font-bold text-gray-800">Olakh Business Card</p>
                                <p className="text-gray-500 mt-2">A dedicated card for your professional brand.</p>
                            </div>
                        </div>
                        <div>
                            <Briefcase className="w-16 h-16 text-gray-800 mb-4 p-3 bg-gray-200 rounded-2xl" />
                            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
                                Professional Networking, Simplified.
                            </h2>
                            <p className="text-xl text-gray-600 mb-6">
                                Create a distinct, professional Business Card separate from your personal contacts. Manage multiple identities and share only what's relevant in any professional setting.
                            </p>
                            {/* <a
                href="#"
                className="inline-flex items-center text-indigo-600 font-semibold text-lg group"
              >
                Explore Business Features
                <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
              </a> */}
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-indigo-600">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-6">
                            Ready to Upgrade Your Contacts?
                        </h2>
                        <p className="text-xl text-indigo-200 mb-8">
                            Stop exchanging paper and start connecting intelligently.
                        </p>
                        <button
                            onClick={handleDownloadClick}
                            className="inline-flex items-center justify-center px-12 py-4 border border-transparent text-lg font-bold rounded-full text-indigo-600 bg-white hover:bg-gray-100 transition duration-300 shadow-xl shadow-indigo-800/50 transform hover:scale-[1.05]"
                        >
                            Start Connecting
                        </button>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default HomePage;
